import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { useState } from "react";
import { styled, InputAdornment, TextField as MUITextField, } from "@mui/material";
import { convertPxToRem } from "../../util-functions";
import { Icon } from "../Icon";
import iconLibrary from "../Icon/icon-library";
import { IconButton } from "../IconButton";
const StyledInput = styled(MUITextField, {
    shouldForwardProp: (prop) => prop !== "startAdornment" && prop !== "endAdornment" && prop !== "error",
})(({ theme, startAdornment, error }) => ({
    "& .MuiFilledInput-root": Object.assign(Object.assign({ backgroundColor: theme.palette.grey03.main, borderRadius: convertPxToRem(19), height: convertPxToRem(52), minHeight: convertPxToRem(52), padding: `0 ${convertPxToRem(16)}` }, theme.typography.largeText), { "&:hover": {
            backgroundColor: theme.palette.grey03.main,
        }, "&.Mui-focused": {
            outline: `${convertPxToRem(2)} solid ${error ? theme.palette.error.main : theme.palette.primaryBlack.main}`,
        }, "&.Mui-disabled": {
            backgroundColor: `${theme.palette.grey03.main}`,
        }, 
        // startAdornment & endAdornment
        p: {
            color: theme.palette.tertiaryText.main,
        }, div: {
            display: "flex",
            marginTop: `0 !important`,
            textTransform: "uppercase",
        }, 
        // startAdornment
        "div:first-of-type": {
            minWidth: startAdornment ? convertPxToRem(24) : 0,
            marginRight: (() => {
                if (startAdornment) {
                    if (typeof startAdornment === "string" &&
                        startAdornment.length === 3) {
                        return convertPxToRem(16);
                    }
                    return convertPxToRem(8);
                }
                return 0;
            })(),
        } }),
    "& .MuiFilledInput-input": {
        paddingLeft: 0,
        paddingTop: convertPxToRem(14),
        paddingBottom: 0,
        color: error
            ? theme.palette.error.main
            : theme.palette.primaryBlackText.main,
    },
    label: Object.assign(Object.assign({ color: theme.palette.secondaryText.main, top: convertPxToRem(3), paddingLeft: (() => {
            if (startAdornment) {
                if (typeof startAdornment === "string" && startAdornment.length === 3) {
                    return convertPxToRem(62);
                }
                return convertPxToRem(49);
            }
            return convertPxToRem(6);
        })(), paddingBottom: convertPxToRem(6) }, theme.typography.smallText), { "&.Mui-focused": {
            color: error ? theme.palette.error.main : theme.palette.primaryBlack.main,
        }, "&.Mui-disabled": {
            color: theme.palette.disabledText.main,
        }, "&[data-shrink=false]": Object.assign({ marginTop: convertPxToRem(-4) }, theme.typography.largeText) }),
    "& .MuiFormHelperText-root": Object.assign(Object.assign({ color: error ? theme.palette.error.main : theme.palette.secondaryText.main, marginLeft: convertPxToRem(16), marginTop: convertPxToRem(8) }, theme.typography.smallText), { "&.Mui-disabled": {
            color: theme.palette.disabledText.main,
        } }),
}));
const StyledInlineInput = styled(StyledInput)(({ theme }) => ({
    "& .MuiFilledInput-root": Object.assign({ borderRadius: convertPxToRem(13), height: convertPxToRem(36), minHeight: convertPxToRem(36), padding: `0 ${convertPxToRem(12)}` }, theme.typography.mediumText),
    "& .MuiFilledInput-input": {
        padding: `${convertPxToRem(8)} ${convertPxToRem(12)} ${convertPxToRem(8)} 0`,
    },
}));
const StyledMultilineInput = styled(StyledInput)(() => ({
    "& .MuiFilledInput-root": {
        height: "auto",
        minHeight: "unset",
        padding: `${convertPxToRem(19)} ${convertPxToRem(16)} ${convertPxToRem(8)}`,
        position: "relative",
    },
    "& .MuiInputAdornment-root": {
        position: "absolute",
        top: `${convertPxToRem(16)}`,
        right: `${convertPxToRem(8)}`,
    },
    "& .MuiFilledInput-input": {
        paddingTop: 0,
    },
}));
export const Input = ({ defaultValue, disabled = false, error = false, helperText, label, onChange, inline = false, multiline = false, required = false, rows = 2, testId, type = "text", startAdornment, endAdornment, value, autoComplete = "off", }) => {
    const [isPasswordVisible, setIsPasswordVisible] = useState(false);
    let finalStartAdornment = startAdornment;
    let finalEndAdornment = endAdornment;
    const finalType = type === "password" ? (isPasswordVisible ? "text" : "password") : type;
    if (startAdornment) {
        finalStartAdornment =
            startAdornment in iconLibrary ? (_jsx(Icon, { iconName: startAdornment, color: disabled ? "disabledText" : "primaryBlack" })) : (startAdornment.substring(0, 3));
    }
    // custom end adornment only allowed if not a password type
    if (endAdornment && type !== "password") {
        finalEndAdornment =
            endAdornment in iconLibrary ? (_jsx(Icon, { iconName: endAdornment, color: disabled ? "disabledText" : "primaryBlack" })) : (endAdornment.substring(0, 3));
    }
    // if password type, use icon button to toggle character visibility
    // multiline inputs do not have type attributes, so the password toggle
    if (type === "password" && !multiline) {
        finalEndAdornment = (_jsx(IconButton, { iconName: isPasswordVisible ? "VisibilityOff" : "VisibilityOn", disabled: disabled, onClick: () => setIsPasswordVisible((currentValue) => !currentValue), variant: "secondary", testId: isPasswordVisible
                ? `${label}-hide-password`
                : `${label}-show-password` }));
    }
    const startAdornmentProp = {
        startAdornment: (_jsx(InputAdornment, Object.assign({ position: "start" }, { children: finalStartAdornment }))),
    };
    const endAdornmentProp = {
        endAdornment: (_jsx(InputAdornment, Object.assign({ position: "end" }, { children: finalEndAdornment }))),
    };
    const inputProps = Object.assign(Object.assign({ disableUnderline: true }, (endAdornment || (type === "password" && !multiline)
        ? endAdornmentProp
        : {})), (startAdornment && !multiline ? startAdornmentProp : {}));
    return (_jsxs(_Fragment, { children: [!multiline && !inline && (_jsx(StyledInput, { id: label, "data-testid": testId, variant: "filled", type: finalType, label: label, defaultValue: defaultValue, startAdornment: finalStartAdornment, endAdornment: finalEndAdornment, helperText: helperText, required: required, onChange: onChange, error: error, disabled: disabled, fullWidth: true, InputProps: inputProps, value: value, autoComplete: autoComplete })), inline && (_jsx(StyledInlineInput, { id: label, "data-testid": testId, variant: "filled", type: finalType, "aria-label": label, placeholder: label, defaultValue: defaultValue, startAdornment: finalStartAdornment, endAdornment: finalEndAdornment, required: required, onChange: onChange, error: error, disabled: disabled, fullWidth: true, InputProps: inputProps, value: value, autoComplete: autoComplete })), multiline && (_jsx(StyledMultilineInput, { id: label, "data-testid": testId, variant: "filled", label: label, defaultValue: defaultValue, endAdornment: finalEndAdornment, helperText: helperText, required: required, onChange: onChange, error: error, disabled: disabled, multiline: true, rows: rows, fullWidth: true, InputProps: inputProps, value: value }))] }));
};
